import { type QueryKey } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ExecutionRunRow } from '@/features/executionRuns/components/ExecutionRunsTableV2Rows';
import { useExecutionRunsTableRowActions } from '@/features/executionRuns/components/useExecutionRunsTableRowActions';
import { useUpdateExecutionRun } from '@/features/executionRuns/hooks/useExecutionRunMutations';
import { SuiteRunEditDialog } from '@/features/executions/components/SuiteRunEditDialog';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useSuiteRunsTableRowActions } from '@/features/executions/components/useSuiteRunsTableRowActions';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { updateSuiteRun } from '@/features/executions/data/suiteRuns';
import { updateTestRun } from '@/features/executions/data/testRuns';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import {
  TableV2ActionsHeaderIcon,
  TableV2SortableHeader,
  tableV2ChevronColumnWidth,
  tableV2MinimumVisibleRows,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { type TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { buildTagColorByName } from '@/features/shared/tags';
import { suiteRunStatusOptions } from '@/shared/constants/statusOptions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { gridPaths, queryKeys, useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { Box, TableCell, TableRow } from '@/ui/primitives';
import type { TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type {
  ExecutionRunDto,
  ProjectDto,
  QueryResult,
  SuiteRunDto,
  TestRunDto,
} from '@flowqa/shared';

type ExecutionRunsTableV2Props = {
  search?: string;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<ExecutionRunDto>>;
  queryKey?: QueryKey;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  minimumVisibleRows?: number;
  onQueryMeta?: (meta: { total: number; items: ExecutionRunDto[] } | null) => void;
  projectOptions?: Array<
    Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'id' | 'name'>
  >;
  showResultsFileName?: boolean;
  tagOptionsOverride?: TagOption[];
  active?: boolean;
  sx?: SxProps<Theme>;
};

const defaultSort: ApiSort = { field: 'startedAt', direction: 'desc' };
const skeletonColumns = [
  'chevron',
  'name',
  'status',
  'passRate',
  'duration',
  'dateTime',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

type SortableExecutionRunsField = 'startedAt' | 'resultsFileCreatedAt';

export const ExecutionRunsTableV2 = ({
  active = true,
  fixedFilters,
  forceLoading = false,
  initialPageSize = 10,
  minimumVisibleRows = tableV2MinimumVisibleRows,
  noRowsText = 'No data',
  onQueryMeta,
  pageSizeOptions = [10, 25, 50],
  projectOptions = [],
  queryFn,
  queryKey,
  search,
  showResultsFileName = true,
  sx,
  tagOptionsOverride,
}: ExecutionRunsTableV2Props): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const showProjectColumn = selectedProjectId === null;
  const [sort, setSort] = useState<ApiSort>(defaultSort);
  const [expandedRunIds, setExpandedRunIds] = useState<Set<string>>(() => new Set());
  const [editTarget, setEditTarget] = useState<ExecutionRunDto | null>(null);
  const [suiteRunEditTarget, setSuiteRunEditTarget] = useState<SuiteRunDto | null>(null);
  const [testRunEditTarget, setTestRunEditTarget] = useState<TestRunDto | null>(null);
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const projectById = useMemo(
    () => new Map(projectOptions.map((project) => [project.id, project])),
    [projectOptions],
  );
  const updateExecutionRun = useUpdateExecutionRun();
  const {
    deleteLoading,
    deleteTarget,
    deleteRowPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
  } = useExecutionRunsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: suiteRunDeleteLoading,
    deleteTarget: suiteRunDeleteTarget,
    deleteRowPendingId: suiteRunDeleteRowPendingId,
    onDeleteClose: onSuiteRunDeleteClose,
    onDeleteConfirm: onSuiteRunDeleteConfirm,
    onDeleteRow: onSuiteRunDeleteRow,
  } = useSuiteRunsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: testRunDeleteLoading,
    deleteTarget: testRunDeleteTarget,
    deleteRowPendingId: testRunDeleteRowPendingId,
    onDeleteClose: onTestRunDeleteClose,
    onDeleteConfirm: onTestRunDeleteConfirm,
    onDeleteRow: onTestRunDeleteRow,
  } = useTestRunsTableRowActions({ selectedProjectId });
  const { groupErroredWithFailed, resolveGroupErroredWithFailed } = useProjectStatusPresentation();
  const editEditor = useDetailEditEditor<ExecutionRunDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Execution run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTarget) return;
      const startedAt = timestamp ? timestamp.toISOString() : null;
      const finishedAt =
        timestamp && durationMs !== null && durationMs !== undefined
          ? new Date(timestamp.getTime() + durationMs).toISOString()
          : null;
      await updateExecutionRun.mutateAsync({
        id: editTarget.id,
        tags,
        startedAt,
        finishedAt,
        durationMs,
        status,
      });
    },
  });
  const suiteRunEditEditor = useDetailEditEditor<SuiteRunDto['status']>({
    projectId: suiteRunEditTarget?.projectId ?? null,
    successMessage: 'Suite run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!suiteRunEditTarget) return;
      await updateSuiteRun(
        { id: suiteRunEditTarget.id, tags, status, timestamp, durationMs },
        suiteRunEditTarget.organisationId ?? selectedOrganisationId ?? null,
      );
    },
  });
  const testRunEditEditor = useDetailEditEditor<TestRunDto['status']>({
    projectId: testRunEditTarget?.projectId ?? null,
    successMessage: 'Test run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!testRunEditTarget) return;
      await updateTestRun(
        { id: testRunEditTarget.id, tags, status, timestamp, durationMs },
        testRunEditTarget.organisationId ?? selectedOrganisationId ?? null,
      );
    },
  });
  const sorts = useMemo<GridQueryInput['sorts']>(() => [sort], [sort]);
  const resolvedQueryKey = useMemo<QueryKey>(
    () => queryKey ?? queryKeys.dataGrid(gridPaths.executionRuns),
    [queryKey],
  );
  const resolvedQueryFn = useMemo(
    () =>
      async (input: GridQueryInput): Promise<QueryResult<ExecutionRunDto>> => {
        if (!queryFn) throw new Error('ExecutionRunsTableV2 requires a queryFn');
        return await queryFn(input);
      },
    [queryFn],
  );
  const editTimestamp = editTarget?.startedAt ?? editTarget?.finishedAt ?? null;
  const resolvedSkeletonColumns = useMemo(
    () =>
      showProjectColumn
        ? [...skeletonColumns.slice(0, -1), 'project' as const, ...skeletonColumns.slice(-1)]
        : skeletonColumns,
    [showProjectColumn],
  );

  const handleSortChange = (field: SortableExecutionRunsField): void => {
    setExpandedRunIds(new Set());
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const toggleExpandedRun = (runId: string): void => {
    setExpandedRunIds((current) => {
      const next = new Set(current);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  };

  const renderSortableHeader = (field: SortableExecutionRunsField, label: string): ReactElement => {
    return (
      <TableV2SortableHeader
        activeSort={sort}
        field={field}
        label={label}
        onSortChange={handleSortChange}
      />
    );
  };

  useEffect(() => {
    if (!active) return;
    setExpandedRunIds(new Set());
  }, [active, fixedFilters, search]);

  const colgroup = (
    <colgroup>
      <col width={tableV2ChevronColumnWidth} />
      <col width={showProjectColumn ? '19%' : '23%'} />
      <col width={showProjectColumn ? '8%' : '9%'} />
      <col width={showProjectColumn ? '13%' : '15%'} />
      <col width={showProjectColumn ? '8%' : '9%'} />
      <col width={showProjectColumn ? '12%' : '14%'} />
      <col width={showProjectColumn ? '12%' : '13%'} />
      <col width={showProjectColumn ? '11%' : '13%'} />
      {showProjectColumn ? <col width="13%" /> : null}
      <col width="4%" />
    </colgroup>
  );

  const header = (
    <TableRow sx={{ backgroundColor: 'background.paper' }}>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>Execution Run</TableCell>
      <TableCell>Status</TableCell>
      <TableCell>Pass Rate</TableCell>
      <TableCell>Duration</TableCell>
      <TableCell>{renderSortableHeader('startedAt', 'Ran At')}</TableCell>
      <TableCell>{renderSortableHeader('resultsFileCreatedAt', 'Uploaded At')}</TableCell>
      <TableCell>Tags</TableCell>
      {showProjectColumn ? <TableCell>Project</TableCell> : null}
      <TableCell sx={{ height: '52px', p: s('none'), width: s('5xl') }}>
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
          <TableV2ActionsHeaderIcon />
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Execution runs"
        colgroup={colgroup}
        emptyText={noRowsText}
        entityLabel="execution runs"
        entitySingularLabel="execution run"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={header}
        initialPageSize={initialPageSize}
        minimumVisibleRows={minimumVisibleRows}
        pageSizeOptions={pageSizeOptions}
        queryFn={resolvedQueryFn}
        queryKey={resolvedQueryKey}
        search={search}
        skeletonColumns={resolvedSkeletonColumns}
        sorts={sorts}
        sx={sx}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <ExecutionRunRow
            key={row.id}
            deleteRowPendingId={deleteRowPendingId}
            editRowPendingId={editEditor.isPending ? (editTarget?.id ?? null) : null}
            expanded={expandedRunIds.has(row.id)}
            groupErroredWithFailed={groupErroredWithFailed}
            isLastVisibleRow={context.isLastVisibleRow}
            organisationId={selectedOrganisationId}
            project={projectById.get(row.projectId) ?? null}
            resolveGroupErroredWithFailed={resolveGroupErroredWithFailed}
            row={row}
            showProjectColumn={showProjectColumn}
            showResultsFileName={showResultsFileName}
            suiteRunDeleteRowPendingId={suiteRunDeleteRowPendingId}
            suiteRunEditRowPendingId={
              suiteRunEditEditor.isPending ? (suiteRunEditTarget?.id ?? null) : null
            }
            tagColorByName={tagColorByName}
            testRunDeleteRowPendingId={testRunDeleteRowPendingId}
            testRunEditRowPendingId={
              testRunEditEditor.isPending ? (testRunEditTarget?.id ?? null) : null
            }
            onDelete={onDeleteRow}
            onDeleteSuiteRun={onSuiteRunDeleteRow}
            onDeleteTestRun={onTestRunDeleteRow}
            onEdit={setEditTarget}
            onEditSuiteRun={setSuiteRunEditTarget}
            onEditTestRun={setTestRunEditTarget}
            onToggle={() => {
              toggleExpandedRun(row.id);
            }}
          />
        )}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Execution Run"
        entityName={editTarget?.id ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        status={editTarget?.status}
        statusOptions={suiteRunStatusOptions}
        timestamp={editTimestamp}
        durationMs={editTarget?.durationMs ?? null}
        timestampLabel="Timestamp"
        durationLabel="Duration"
        durationInputMode="timeParts"
        loading={editEditor.isPending || updateExecutionRun.isPending}
        cascadeCopy="Tags will be applied to all associated suite runs and test runs."
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await editEditor.save({ tags, colors, status, timestamp, durationMs });
          setEditTarget(null);
        }}
      />
      <SuiteRunEditDialog
        open={suiteRunEditTarget !== null}
        execution={suiteRunEditTarget}
        loading={suiteRunEditEditor.isPending}
        onClose={() => {
          if (suiteRunEditEditor.isPending) return;
          setSuiteRunEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await suiteRunEditEditor.save({ tags, colors, status, timestamp, durationMs });
          setSuiteRunEditTarget(null);
        }}
      />
      <TestRunEditDialog
        open={testRunEditTarget !== null}
        execution={testRunEditTarget}
        loading={testRunEditEditor.isPending}
        onClose={() => {
          if (testRunEditEditor.isPending) return;
          setTestRunEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await testRunEditEditor.save({ tags, colors, status, timestamp, durationMs });
          setTestRunEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'executionRun' })}
        description="This execution run and its associated suite runs and test runs will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'executionRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={suiteRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'suiteRun' })}
        description="This suite run and its associated test runs will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'suiteRun' })}
        confirmColor="error"
        loading={suiteRunDeleteLoading}
        onClose={onSuiteRunDeleteClose}
        onConfirm={onSuiteRunDeleteConfirm}
      />
      <ConfirmDialog
        open={testRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'testRun' })}
        confirmColor="error"
        loading={testRunDeleteLoading}
        onClose={onTestRunDeleteClose}
        onConfirm={onTestRunDeleteConfirm}
      />
    </>
  );
};
